import { useState, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Body2,
  Caption1,
  Badge,
  Button,
  Input,
  Dropdown,
  Option,
  Field,
  Spinner,
  MessageBar,
  MessageBarBody,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  Avatar,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import { Add24Regular, ShieldCheckmark24Regular, ShieldProhibited24Regular } from '@fluentui/react-icons';
import { DataTable, type Column } from '@/components/DataTable';
import { useSharePointList } from '@/hooks/useSharePointList';
import type {
  AppAccessEntry,
  AccessStatus,
  Employee,
  SPListItem,
} from '@/models/types';
import { useAppContext } from '@/store/appContext';
import { canManageRoles } from '@/utils/permissions';
import { AccessDenied } from '@/components/AccessDenied';
import { formatDateTime, getInitials } from '@/utils/formatters';

const STATUS_COLORS: Record<AccessStatus, 'success' | 'danger'> = {
  Active: 'success',
  Revoked: 'danger',
};

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '20px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  metric: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '12px',
  },
  metricCard: {
    padding: '14px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
  },
  metricValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: tokens.colorBrandForeground1,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '14px',
  },
  memberRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
});

export function MemberAccess() {
  const styles = useStyles();
  const { currentUser, listIds } = useAppContext();

  if (currentUser && !canManageRoles(currentUser.role)) {
    return <AccessDenied reason="Member access management is restricted to Admins." />;
  }

  const { items: entries, loading, create, update } =
    useSharePointList<AppAccessEntry>('OC_AppAccessList', {
      autoFetch: !!listIds,
    });
  const { items: employees } = useSharePointList<Employee>('OC_Employees', {
    autoFetch: !!listIds,
  });

  const [showForm, setShowForm] = useState(false);
  const [pickedEmpId, setPickedEmpId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const employeeMap = useMemo(() => {
    const map = new Map<string, SPListItem<Employee>>();
    for (const e of employees) {
      if (e.fields.Email) map.set(e.fields.Email.toLowerCase(), e);
    }
    return map;
  }, [employees]);

  const activeCount = entries.filter((e) => e.fields.Status === 'Active').length;
  const revokedCount = entries.filter((e) => e.fields.Status === 'Revoked').length;
  const allEmpsCount = employees.filter((e) => e.fields.Status === 'Active').length;

  // Employees who exist but are NOT in the allow list
  const unaddedEmployees = employees.filter(
    (e) =>
      e.fields.Status === 'Active' &&
      e.fields.Email &&
      !entries.some(
        (a) => a.fields.Email?.toLowerCase() === e.fields.Email.toLowerCase(),
      ),
  );

  const handleAdd = async () => {
    setError(null);
    if (!pickedEmpId) {
      setError('Pick an employee to grant access.');
      return;
    }
    const emp = employees.find((e) => e.id === pickedEmpId);
    if (!emp) {
      setError('Employee not found.');
      return;
    }
    setBusy(emp.id);
    try {
      await create({
        Title: emp.fields.Title,
        Email: emp.fields.Email,
        Status: 'Active',
        GrantedBy: currentUser?.email ?? '',
        GrantedDate: new Date().toISOString(),
        Notes: notes,
      });
      setShowForm(false);
      setPickedEmpId('');
      setNotes('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add');
    } finally {
      setBusy(null);
    }
  };

  const toggleStatus = async (entry: SPListItem<AppAccessEntry>) => {
    setBusy(entry.id);
    try {
      const next: AccessStatus =
        entry.fields.Status === 'Active' ? 'Revoked' : 'Active';
      await update(entry.id, { Status: next });
    } finally {
      setBusy(null);
    }
  };

  const columns: Column<SPListItem<AppAccessEntry>>[] = [
    {
      key: 'name',
      label: 'Member',
      sortable: true,
      getValue: (item) => item.fields.Title,
      render: (item) => {
        const emp = employeeMap.get(item.fields.Email?.toLowerCase() ?? '');
        return (
          <div className={styles.memberRow}>
            <Avatar
              name={item.fields.Title}
              initials={getInitials(item.fields.Title)}
              size={28}
              color="brand"
            />
            <div style={{ minWidth: 0 }}>
              <Body2 style={{ fontWeight: 500 }}>{item.fields.Title}</Body2>
              <Caption1>
                {item.fields.Email}
                {emp && ` · ${emp.fields.Department || emp.fields.JobTitle || ''}`}
              </Caption1>
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      getValue: (item) => item.fields.Status,
      render: (item) => (
        <Badge
          appearance="filled"
          color={STATUS_COLORS[item.fields.Status]}
          size="small"
        >
          {item.fields.Status}
        </Badge>
      ),
    },
    {
      key: 'granted',
      label: 'Granted',
      sortable: true,
      getValue: (item) => item.fields.GrantedDate,
      render: (item) => (
        <div>
          <Caption1>{formatDateTime(item.fields.GrantedDate)}</Caption1>
          <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
            by {item.fields.GrantedBy?.split('@')[0] ?? '—'}
          </Caption1>
        </div>
      ),
    },
    {
      key: 'notes',
      label: 'Notes',
      getValue: (item) => item.fields.Notes ?? '',
      render: (item) => <Caption1>{item.fields.Notes}</Caption1>,
    },
    {
      key: 'actions',
      label: '',
      width: '120px',
      render: (item) => (
        <Button
          appearance={item.fields.Status === 'Active' ? 'outline' : 'primary'}
          size="small"
          icon={
            busy === item.id ? (
              <Spinner size="tiny" />
            ) : item.fields.Status === 'Active' ? (
              <ShieldProhibited24Regular />
            ) : (
              <ShieldCheckmark24Regular />
            )
          }
          disabled={busy === item.id}
          onClick={() => toggleStatus(item)}
        >
          {item.fields.Status === 'Active' ? 'Revoke' : 'Restore'}
        </Button>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <PageTitle>Member Access</PageTitle>
        <Button
          appearance="primary"
          icon={<Add24Regular />}
          onClick={() => setShowForm(true)}
        >
          Grant access
        </Button>
      </div>

      <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
        Only members on this list (with Status = Active) can use Prism.
        Everyone else sees "Access Pending" after sign-in. Admins always have
        access regardless of this list.
      </Body1>

      <div className={styles.metric}>
        <div className={styles.metricCard}>
          <Caption1>Active members</Caption1>
          <div className={styles.metricValue}>{activeCount}</div>
        </div>
        <div className={styles.metricCard}>
          <Caption1>Revoked</Caption1>
          <div className={styles.metricValue}>{revokedCount}</div>
        </div>
        <div className={styles.metricCard}>
          <Caption1>Total employees</Caption1>
          <div className={styles.metricValue}>{allEmpsCount}</div>
        </div>
        <div className={styles.metricCard}>
          <Caption1>Not yet granted</Caption1>
          <div className={styles.metricValue}>{unaddedEmployees.length}</div>
        </div>
      </div>

      <DataTable
        columns={columns}
        items={entries}
        loading={loading}
        searchPlaceholder="Search by name or email..."
        searchFields={(item) =>
          `${item.fields.Title} ${item.fields.Email} ${item.fields.Notes ?? ''}`
        }
        getRowKey={(item) => item.id}
        emptyMessage='No members granted yet. Click "Grant access" to add the first.'
      />

      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Grant Prism access</DialogTitle>
            <DialogContent>
              {error && (
                <MessageBar intent="error" style={{ marginBottom: 12 }}>
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}
              <div className={styles.form}>
                <Field label="Employee" required>
                  <Dropdown
                    placeholder={`Pick from ${unaddedEmployees.length} employees not yet granted`}
                    value={
                      employees.find((e) => e.id === pickedEmpId)?.fields.Title ?? ''
                    }
                    onOptionSelect={(_, d) => setPickedEmpId(d.optionValue as string)}
                  >
                    {unaddedEmployees.map((e) => (
                      <Option
                        key={e.id}
                        value={e.id}
                        text={`${e.fields.Title} (${e.fields.Email})`}
                      >
                        {`${e.fields.Title} (${e.fields.Email})`}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
                <Field label="Notes (optional)">
                  <Input
                    value={notes}
                    onChange={(_, d) => setNotes(d.value)}
                    placeholder="Why is this person being granted access?"
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleAdd}
                disabled={!pickedEmpId || !!busy}
                icon={busy ? <Spinner size="tiny" /> : <Add24Regular />}
              >
                Grant access
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
